/**
 * Notifications Vuex module for Portal Application
 * 
 * @module Notifications
 * @see
 * @author Maureen Ronquillo (Fresche Solutions)
 * @date March 2025
 */

import portalapi from "@services/portalapi";
import { resetStateData } from "@/utils/vuexStoreUtils";
import { getField, updateField } from "vuex-map-fields";

const DEFAULT_STATE = {
    userNotifications: [],
    notificationLoadingCount: 0,
};

const state = structuredClone(DEFAULT_STATE);

const getters = {
    getField,

    getUserNotifications(st) {
        return st.userNotifications;
    },

    getNotificationLoadingCount(st) {
        return st.notificationLoadingCount ?? 0;
    },
};

const mutations = {
    updateField, // for vuex-map-fields

    async clearStateData(st) {
        st = resetStateData(st, DEFAULT_STATE);
    },

    setUserNotifications(st, payload) {
        st.userNotifications = payload;
    },

    incrementNotificationLoadingCount(st) {
        st.notificationLoadingCount++;
    },

    decrementNotificationLoadingCount(st) {
        st.notificationLoadingCount--;
    },
};

const actions = {
    // Get notifications
    async fetchUserNotifications({ commit }, page = 1)
    {
        commit("incrementNotificationLoadingCount");
        const response = await portalapi.moduleUserNotifications.getUserNotifications(page);
        commit("decrementNotificationLoadingCount");

        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            await commit("setUserNotifications", response.data);
        }
    },


    // Delete notification
    async deleteNotification({ commit }, id)
    {
        commit("incrementNotificationLoadingCount");
        const response = await portalapi.moduleUserNotifications.deleteNotification(id);
        commit("decrementNotificationLoadingCount");
        
        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            // Refresh notifications after deletion
            await actions.fetchUserNotifications({ commit });

            return true;
        }

        return false;
    },


    // Mark notification as seen
    async markNotificationAsSeen({ commit }, id)
    {
        commit("incrementNotificationLoadingCount");
        const response = await portalapi.moduleUserNotifications.markNotificationAsSeen(id);
        commit("decrementNotificationLoadingCount");
        
        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            // Refresh notifications after marking as seen
            await actions.fetchUserNotifications({ commit });

            return true;
        }
        
        return false;
    },

    async checkUserLoginNotifications({ commit }) 
    {
        commit("incrementNotificationLoadingCount");
        const response = await portalapi.moduleUserNotifications.checkUserLoginNotifications();
        commit("decrementNotificationLoadingCount");

        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            await commit("setUserNotifications", response.data);

            return true;
        }
        return false;
    },

    // Delete all notifications for user
    async clearAllUserNotifications({ commit })
    {
        commit("incrementNotificationLoadingCount");
        const response = await portalapi.moduleUserNotifications.clearAllUserNotifications();
        commit("decrementNotificationLoadingCount");

        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            // Refresh notifications after deletion
            await actions.fetchUserNotifications({ commit });

            return true;
        }
        
        return false;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
